import {IdentityProviderGroup} from "@/models/IdentityProviderGroup.ts";
import React, {FC, ReactElement, useEffect, useState} from "react";
import Modal from 'react-modal';
import {useLocale} from "@/contexts/localeContext.tsx";
import WizardSideBarStep from "@/models/WizardSideBarStep.ts";
import {TranslationKeys} from "@/contexts/TranslationKeys.ts";
import {Form, Formik} from "formik";
import WizardTwo from "@/components/wizard/wizardTwo.tsx";
import LinkGroupProfile from "@/components/map-groups/LinkGroupProfile.tsx";
import SelectGroups from "@/components/map-groups/SelectGroups.tsx";
import Summary from "@/components/map-groups/summary.tsx";
import GroupDescription from "@/components/map-groups/GroupDescription.tsx";
import {saveIdentityProviderGroupMap} from "@/services/mobileAccountService.ts";
import {toast} from "react-toastify";
import {getErrorMessage} from "@/services/utilities/ServiceError.ts";
import {useDialog} from "@/components/dialog/DialogContext.tsx";

type Props = {
    onClose: () => void
    isOpen: boolean
    account: any;
    currentGroup: IdentityProviderGroup;
    setBusy: (boolean) => void;
    setSelectedGroup: (group: IdentityProviderGroup) => void;
};

const MapGroupWizard: FC<Props> = ({onClose, isOpen, account, setSelectedGroup, currentGroup, setBusy}) => {
    const {translate} = useLocale();
    const [currentStep, setCurrentStep] = useState(0);
    const dialog = useDialog();
    const [stepDescriptions, setStepDescriptions] = useState<{ [key: string]: string }>({
        groupName: translate.byKey(TranslationKeys.pending),
        profileName: translate.byKey(TranslationKeys.pending),
    });

    useEffect(() => {
        if (!isOpen) {
            resetKeyField();
        }
    }, [isOpen])
    /**
     *
     * @param event
     * This function basically reset everything to original state.
     */
    const resetKeyField = () => {
        setCurrentStep(0);
    }

    const onMapSave = (values) => {
        const newMap = {
            id: values.id,
            idpGroupName: values.idpGroupName,
            identityProviderGroupId: values.identityProviderGroupId,
            idpGroupDescription: values.idpGroupDescription,
            profileName: values.profileName,
            profileId: values.profileId,
            accountName: account.name,
            accountId: account.id,
        }
        saveItem(newMap);
        closeWizard();
    }

    const saveItem = (item) => {
        const createNew = parseInt(item.id) === 0;
        setBusy(true)
        saveIdentityProviderGroupMap(item, createNew).then(async (value) => {
            toast.success(translate.byKey(TranslationKeys.groupSaved));
        }).catch(error => {
            dialog.pushError({title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error)});
        }).finally(() => {
            setBusy(false);
        })
    }

    const closeWizard = () => {
        setStepDescriptions({
            groupName: translate.byKey(TranslationKeys.pending),
            profileName: translate.byKey(TranslationKeys.pending),
        });
        setSelectedGroup({
            id: 0,
            idpGroupName: "",
            identityProviderGroupId: "",
            idpGroupDescription: "",
            profileName: "",
            profileId: 0,
            accountName: account.accountName,
            accountId: account.id,
        });
        onClose();
    }

    const editWizardStepContent: ReactElement[] = [
        <LinkGroupProfile
            onNext={() => setCurrentStep(currentStep + 1)}
            setStepDescriptions={setStepDescriptions}
            title={translate.byKey(TranslationKeys.selectProfile)}/>,
        <GroupDescription
            onBack={() => setCurrentStep(currentStep - 1)}
            onNext={() => setCurrentStep(currentStep + 1)}
            title={translate.byKey(TranslationKeys.description)}/>,
        <Summary
            onBack={() => setCurrentStep(currentStep - 1)}/>
    ];

    const wizardStepContent: ReactElement[] = [
        <SelectGroups
            onNext={() => setCurrentStep(currentStep + 1)}
            setStepDescriptions={setStepDescriptions}
            currentAccountId={account.id}
            setSelectedGroup={setSelectedGroup}
            title={translate.byKey(TranslationKeys.selectIdpGroup)}/>,
        <LinkGroupProfile
            onBack={() => setCurrentStep(currentStep - 1)}
            onNext={() => setCurrentStep(currentStep + 1)}
            setStepDescriptions={setStepDescriptions}
            title={translate.byKey(TranslationKeys.selectProfile)}/>,
        <GroupDescription
            onBack={() => setCurrentStep(currentStep - 1)}
            onNext={() => setCurrentStep(currentStep + 1)}
            title={translate.byKey(TranslationKeys.description)}/>,
        <Summary
            onBack={() => setCurrentStep(currentStep - 1)}/>
    ];

    const editWizardSideBarSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.selectProfile),
            description: stepDescriptions.profileName,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.description),
            description: '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        }
    ];

    const wizardSideBarSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.selectIdpGroup),
            description: stepDescriptions.groupName,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.selectPermission),
            description: stepDescriptions.profileName,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.description),
            description: '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        }
    ];

    const getWizardContentSteps = () => {
        if (currentGroup.id != 0) {
            return editWizardStepContent;
        }
        return wizardStepContent;
    }

    const getWizardSidebarSteps = () => {
        if (currentGroup.id != 0) {
            return editWizardSideBarSteps;
        }
        return wizardSideBarSteps;
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none w-content">
        <Formik initialValues={currentGroup} onSubmit={onMapSave} enableReinitialize>
            <Form>
                <WizardTwo
                    onClose={closeWizard}
                    step={currentStep}
                    name={translate.byKey(TranslationKeys.mapIdpGroup)}
                    sidebarSteps={getWizardSidebarSteps()}
                    stepContent={getWizardContentSteps()}/>
            </Form>
        </Formik>
    </Modal>
}

export default MapGroupWizard;
